import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, Form } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { Router, ActivatedRoute } from '@angular/router';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { UtilityService } from 'src/app/services/utility.service';
import { UserService } from 'src/app/services/user.service';
import { ApplicationService } from '../../services/application.service';
import { RegistrationService } from '../../services/registration.service';
import { CompanyService } from '../../services/company.service';

import { MessageListItem } from '../../models/application';
import { ForgotPasswordInfo } from '../../models/registration';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss']
})
export class ForgotPasswordComponent implements OnInit {
  @ViewChild('securityQuestionAnswerRef', { static: false }) securityQuestionAnswerRef: ElementRef;

  private _handleError: HandleError;
  private _logging = environment.logging;
  fieldValidations = environment.fieldValidations;

  recaptchaSiteKey = environment.restAPI.recaptchaSiteKey;

  // isBrowserIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showInstructionMessagesSpinner = false;
  instructionMessageList: MessageListItem[] = [];

  forgotPasswordFormPart1: UntypedFormGroup;
  userNameFormControl = new UntypedFormControl();
  socialSecurityNumberFormControl = new UntypedFormControl();
  recaptchaFormControl = new UntypedFormControl();

  forgotPasswordFormPart2: UntypedFormGroup;
  securityQuestionAnswerFormControl = new UntypedFormControl();

  forgotPasswordFormPart3: UntypedFormGroup;
  emailFormControl = new UntypedFormControl();

  forgotPasswordInfo: ForgotPasswordInfo;
  userVerified = false;

  showVerifyUserSpinner = false;
  showVerifyError = false;
  showSubmitSpinner = false;
  showQuestionError = false;
  showSubmitError = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _formBuilder: UntypedFormBuilder,
    private _errorHandler: ErrorHandler,
    private _utilityService: UtilityService,
    private _userService: UserService,
    private _applicationService: ApplicationService,
    private _registrationService: RegistrationService,
    private _companyService: CompanyService,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('ForgotPasswordComponent');
    this.getData();
    this.scrollToTop();
  }

  ngOnInit() {}

  getData(): void {
    const companyCode: string = this._companyService.getCompanyCode();

    this.forgotPasswordInfo = this._registrationService.getNewForgotPasswordInfo();

    // this.getInstructionMessageList(companyCode);

    this.createForm();
  }

  getInstructionMessageList(companyCode: string): void {
    this.showInstructionMessagesSpinner = true;
    this._applicationService.getWelcomeMessageList(companyCode).subscribe(
      resultArray => {
        this.instructionMessageList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('ForgotPasswordComponent.getInstructionMessageList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('ForgotPasswordComponent.getInstructionMessageList :: result = ' + JSON.stringify(resultArray));
        }
        this.showInstructionMessagesSpinner = false;
      },
      error => this._handleError('getInstructionMessageList', error)
    );
  }

  getMessageCssClass(item: MessageListItem): string {
    return this._applicationService.getMessageListItemDisplayClass(item);
  }

  createForm(): void {
    this.forgotPasswordFormPart1 = this._formBuilder.group({});
    this.forgotPasswordFormPart1.addControl('userNameFormControl', this.userNameFormControl);
    this.forgotPasswordFormPart1.addControl('socialSecurityNumberFormControl', this.socialSecurityNumberFormControl);
    this.forgotPasswordFormPart1.addControl('recaptchaFormControl', this.recaptchaFormControl);

    this.forgotPasswordFormPart2 = this._formBuilder.group({});
    this.forgotPasswordFormPart2.addControl('securityQuestionAnswerFormControl', this.securityQuestionAnswerFormControl);

    this.forgotPasswordFormPart3 = this._formBuilder.group({});
    this.forgotPasswordFormPart3.addControl('emailFormControl', this.emailFormControl);

    this.setFormValues(null);
    this.setFormValidations(false);
  }

  setFormValues(forgotPasswordInfo: ForgotPasswordInfo = null): void {
    if (forgotPasswordInfo === null) {
      forgotPasswordInfo = this._registrationService.getNewForgotPasswordInfo();
    }

    this.forgotPasswordFormPart1.setValue({
      userNameFormControl: forgotPasswordInfo.userName,
      socialSecurityNumberFormControl: forgotPasswordInfo.ssnLastFour,
      recaptchaFormControl: null
    });

    this.forgotPasswordFormPart2.setValue({
      securityQuestionAnswerFormControl: forgotPasswordInfo.securityQuestionAnswer
    });

    this.forgotPasswordFormPart3.setValue({
      emailFormControl: forgotPasswordInfo.homeEmail
    });
  }

  setFormValidations(clearAllValidations: boolean) {
    if (clearAllValidations) {
      this.forgotPasswordFormPart1.get('userNameFormControl').setValidators([]);
      this.forgotPasswordFormPart1.get('socialSecurityNumberFormControl').setValidators([]);
      this.forgotPasswordFormPart1.get('recaptchaFormControl').setValidators([]);

      this.forgotPasswordFormPart2.get('securityQuestionAnswerFormControl').setValidators([]);

      this.forgotPasswordFormPart3.get('emailFormControl').setValidators([]);
    } else {
      this.forgotPasswordFormPart1.get('userNameFormControl').setValidators([Validators.required]);
      this.forgotPasswordFormPart1
        .get('socialSecurityNumberFormControl')
        .setValidators([
          Validators.required,
          Validators.minLength(this.fieldValidations.userRegistration.socialSecurityLastFourNumberMinLength),
          Validators.maxLength(this.fieldValidations.userRegistration.socialSecurityLastFourNumberMaxLength)
        ]);
      //this.forgotPasswordFormPart2.get('securityQuestionAnswerFormControl').setValidators([
      //    Validators.required,
      //    Validators.pattern(this.fieldValidations.userRegistration.securityAnswerPattern)
      //  ]);

      this.forgotPasswordFormPart3
        .get('emailFormControl')
        .setValidators([Validators.required, Validators.pattern(this.fieldValidations.userRegistration.emailAddressPattern)]);
    }

    this.forgotPasswordFormPart1.get('userNameFormControl').markAsPristine();
    this.forgotPasswordFormPart1.get('socialSecurityNumberFormControl').markAsPristine();
    this.forgotPasswordFormPart1.get('recaptchaFormControl').markAsPristine();

    this.forgotPasswordFormPart2.get('securityQuestionAnswerFormControl').markAsPristine();

    this.forgotPasswordFormPart3.get('emailFormControl').markAsPristine();

    this.forgotPasswordFormPart1.get('userNameFormControl').markAsUntouched();
    this.forgotPasswordFormPart1.get('socialSecurityNumberFormControl').markAsUntouched();
    this.forgotPasswordFormPart1.get('recaptchaFormControl').markAsUntouched();

    this.forgotPasswordFormPart2.get('securityQuestionAnswerFormControl').markAsUntouched();

    this.forgotPasswordFormPart3.get('emailFormControl').markAsUntouched();

    this.forgotPasswordFormPart1.get('userNameFormControl').updateValueAndValidity();
    this.forgotPasswordFormPart1.get('socialSecurityNumberFormControl').updateValueAndValidity();
    this.forgotPasswordFormPart1.get('recaptchaFormControl').updateValueAndValidity();

    this.forgotPasswordFormPart2.get('securityQuestionAnswerFormControl').updateValueAndValidity();

    this.forgotPasswordFormPart3.get('emailFormControl').updateValueAndValidity();
  }

  verifyEnabled(): boolean {
    return this.forgotPasswordFormPart1.valid;
  }

  verifyUser() {
    if (this.forgotPasswordFormPart1.valid) {
      this.showVerifyUserSpinner = true;
      this.showVerifyError = false;

      this.forgotPasswordInfo = this._registrationService.getNewForgotPasswordInfo();
      this.forgotPasswordInfo.userName = this.forgotPasswordFormPart1.get('userNameFormControl').value;
      this.forgotPasswordInfo.ssnLastFour = this.forgotPasswordFormPart1.get('socialSecurityNumberFormControl').value;
      this.forgotPasswordInfo.companyCode = this._companyService.getCompanyCode();

      this._registrationService.getForgotPasswordInfo(this.forgotPasswordInfo).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('ForgotPasswordComponent.verifyUser :: result.participantId = ' + result.participantId);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('ForgotPasswordComponent.verifyUser :: result = ' + JSON.stringify(result));
          }
          this.showVerifyUserSpinner = false;

          if (result !== null && result.participantId !== null && result.participantId > 0) {
            this.forgotPasswordInfo.participantId = result.participantId;
            this.forgotPasswordInfo.userId = result.userId;
            this.forgotPasswordInfo.homeEmail = result.homeEmail;
            this.forgotPasswordInfo.homePhone = result.homePhone;
            this.forgotPasswordInfo.cellPhone = result.cellPhone;
            this.forgotPasswordInfo.workPhone = result.workPhone;

            this.forgotPasswordInfo.securityQuestionId = result.securityQuestionId;
            this.forgotPasswordInfo.securityQuestionText = result.securityQuestionText;

            //this.forgotPasswordFormPart2.controls['securityQuestionAnswerFormControl'].setValue(this.forgotPasswordInfo.securityQuestionAnswer);
            this.forgotPasswordFormPart2.controls['securityQuestionAnswerFormControl'].markAsDirty();
            this.forgotPasswordFormPart2.controls['securityQuestionAnswerFormControl'].markAsTouched();
            this.forgotPasswordFormPart2.get('securityQuestionAnswerFormControl').updateValueAndValidity();

            this.forgotPasswordFormPart3.controls['emailFormControl'].setValue(this.forgotPasswordInfo.homeEmail);
            this.forgotPasswordFormPart3.controls['emailFormControl'].markAsDirty();
            this.forgotPasswordFormPart3.controls['emailFormControl'].markAsTouched();
            this.forgotPasswordFormPart3.get('emailFormControl').updateValueAndValidity();

            this.userVerified = true;
            this.showVerifyError = false;

            setTimeout(() => {
              this.securityQuestionAnswerRef.nativeElement.focus();
            });
          } else {
            this.userVerified = false;
            this.showVerifyError = true;
          }
        },
        error => {
          this._handleError('verifyUser', error, this.forgotPasswordInfo);
          this.userVerified = false;
          this.showVerifyError = true;
        }
      );
    } else {
      this.userVerified = false;
      this.showVerifyError = true;
    }
  }

  forgotUserName(): void {
    if (this._logging.navigationTraceLog) {
      console.log('ForgotPasswordComponent.forgotUserName');
    }
    this._router.navigate(['forgotusername']);
  }

  submitEnabled(): boolean {
    return this.forgotPasswordFormPart2.valid && this.forgotPasswordFormPart3.valid;
  }

  resetForm(): void {
    this.userVerified = false;

    this.showVerifyUserSpinner = false;
    this.showVerifyError = false;
    this.showSubmitSpinner = false;
    this.showQuestionError = false;
    this.showSubmitError = false;

    this.forgotPasswordInfo = this._registrationService.getNewForgotPasswordInfo();

    this.setFormValues(null);
    this.setFormValidations(false);

    this.forgotPasswordFormPart1.markAsPristine();
    this.forgotPasswordFormPart1.markAsUntouched();
    this.forgotPasswordFormPart2.markAsPristine();
    this.forgotPasswordFormPart2.markAsUntouched();
    this.forgotPasswordFormPart3.markAsPristine();
    this.forgotPasswordFormPart3.markAsUntouched();
  }

  submitUser(): void {
    if (this.forgotPasswordFormPart2.valid && this.forgotPasswordFormPart3.valid) {
      this.showSubmitSpinner = true;

      this.forgotPasswordInfo.securityQuestionAnswer = this.forgotPasswordFormPart2.get('securityQuestionAnswerFormControl').value;

      this._registrationService.verifyForgottenPasswordSecurityQuestion(this.forgotPasswordInfo).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('ForgotPasswordComponent.submitUser.verifyForgottenPasswordSecurityQuestion :: result = ' + result);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('ForgotPasswordComponent.submitUser.verifyForgottenPasswordSecurityQuestion :: result = ' + result);
          }

          if (Number(result) > 0) {
            this.showSubmitError = false;
            this.showQuestionError = false;

            this._registrationService.sendTemporaryPassword(this.forgotPasswordInfo).subscribe(
              result2 => {
                if (this._logging.serviceCallTraceLog) {
                  console.log('ForgotPasswordComponent.submitUser.sendTemporaryPassword :: result2 = ' + result2);
                }
                if (this._logging.serviceDataTraceLog) {
                  console.log('ForgotPasswordComponent.submitUser.sendTemporaryPassword :: result2 = ' + result2);
                }

                this.showSubmitSpinner = false;

                if (Number(result2) === 0) {
                  this.showSubmitError = false;
                  this.showQuestionError = false;
                  this.openLoginOneTime();
                } else {
                  this.showQuestionError = false;
                  this.showSubmitError = true;
                  this.showSubmitSpinner = false;
                }
              },
              error => {
                this._handleError('submitUser.sendTemporaryPassword', error, this.forgotPasswordInfo);
                this.showSubmitSpinner = false;
                this.showQuestionError = false;
                this.showSubmitError = true;
              }
            );
          } else {
            this.showSubmitSpinner = false;
            this.showQuestionError = true;
            this.showSubmitError = false;
          }
        },
        error => {
          this._handleError('submitUser.verifyForgottenPasswordSecurityQuestion', error, this.forgotPasswordInfo);
          this.showSubmitSpinner = false;
          this.showQuestionError = true;
          this.showSubmitError = false;
        }
      );
    } else {
      this.showSubmitSpinner = false;
      this.showQuestionError = false;
      this.showSubmitError = true;
    }
  }

  openLoginOneTime(): void {
    if (this._logging.navigationTraceLog) {
      console.log('ForgotPasswordComponent.openLoginOneTime');
    }
    this._router.navigate(['loginonetime']);
  }

  maskSocialSecurityNumber(socialSecurityNumber: string): string {
    return socialSecurityNumber.replace(/\d(?=\d{4})/g, '*');
  }

  scrollToTop() {
    const contentStart = document.getElementsByClassName('mat-sidenav-content');
    if (contentStart.length > 0) {
      contentStart[0].scrollTop = 0;
    }
  }
}
